import React, { useState, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import authenticationService from 'auth/services/authenticationService'
import { Button, FormControl } from 'react-bootstrap'
import { AxiosError } from 'axios'
import { formatBirthDateWithDashes, getReturnUrl } from 'auth/utils/authHelpers'
import NumberFormat from 'react-number-format'
import { ErrorMessageLegacy, logError, ValidationError } from 'shared'
import { IGermanyOtpStartRequest } from 'auth/interfaces/i-otpid'
import appConfig from 'app.config'
import { createEmailComponents } from 'shared/utils/emailHelpers'

interface IError {
	customerId: Error | undefined
	birthDate: Error | undefined
	error: Error | undefined
	otp: Error | undefined
}

interface GermanyOtpProps {
	onError?(error: Error): void
}

const loginLink = {
	de: 'https://tfbank.de/blog/kundennummer',
	at: 'https://tfbank.at/tf-bank-blog/kundennummer',
}[appConfig.country]

const GermanyOtp = ({ onError = () => {} }: GermanyOtpProps) => {
	const { t } = useTranslation()
	const [loading, setLoading] = useState(false)
	const [customerId, setCustomerId] = useState('')
	const [birthDate, setBirthDate] = useState('')
	const [otp, setOtp] = useState('')
	const [secret, setSecret] = useState('')
	const [done, setDone] = useState(false)

	const [error, setError] = useState<IError>({
		customerId: undefined,
		birthDate: undefined,
		error: undefined,
		otp: undefined,
	})

	const loginStart = async (birthDate: string, customerId: string) => {
		let err: IError = {
			customerId: undefined,
			birthDate: undefined,
			error: undefined,
			otp: undefined,
		}
		setError(err)

		if (!/^\d{8}$/.test(birthDate)) {
			err.birthDate = new Error(t('InvalidFormat'))
		}

		if (!/^\d{7,10}$/.test(customerId)) {
			err.customerId = new Error(t('InvalidFormat'))
		}
		if (err.birthDate || err.customerId) {
			setError(err)
			return
		}

		const formattedBirthDate = formatBirthDateWithDashes(birthDate)

		setLoading(true)

		try {
			const { secret: newSecret } =
				await authenticationService.germanyOtpStart(
					{
						birthDate: formattedBirthDate,
						customerId: customerId!,
						returnUrl: getReturnUrl(),
					} as IGermanyOtpStartRequest,
					t,
				)
			setSecret(newSecret)
		} catch (e: any) {
			logError(e)
			const ex = e as AxiosError
			if (ex.message.includes('404')) {
				err.error = new Error('CardNotFoundContactSupport')
				setError(err)
				setBirthDate('')
			}
			if (ex.response?.data?.message) {
				const { message }: { message: string } = e.response.data
				if (message.toLowerCase().includes('birthdate')) {
					err.birthDate = new Error(t('BirthdateNoMatch'))
					setError(err)
				} else if (message.toLowerCase().includes('customerid')) {
					err.customerId = new Error(message)
					setError(err)
				}
			} else {
				err.error = new Error(t('AuthenticationError'))
				setError(err)
			}
		}
		setLoading(false)
	}

	const loginComplete = async () => {
		let err: IError = {
			customerId: undefined,
			birthDate: undefined,
			error: undefined,
			otp: undefined,
		}
		setError(err)
		if (!/^\d{4,6}$/.test(otp)) {
			err.otp = new Error(t('OtpInvalid'))
			setError(err)
			return
		}

		setLoading(true)
		try {
			await authenticationService
				.germanyOtpComplete(secret, otp, customerId, t)
				.then(({ redirectUrl, secret: newSecret }) =>
					authenticationService.redirectTo(
						redirectUrl || getReturnUrl(),
						newSecret,
					),
				)
				.then(() => setDone(true))
		} catch (e: any) {
			logError(e)
			console.log(e)
			const ex = e as AxiosError
			if (ex.message.includes('404')) {
				err.error = new Error(t('AuthenticationError'))
				setError(err)
			}
			if (ex.response?.data?.message) {
				const { message }: { message: string } = e.response.data
				if (message.toLowerCase().includes('otp')) {
					setError({
						...error,
						otp: new Error(t('OtpInvalid')),
					})
				} else if (message.toLowerCase().includes('customerid')) {
					setError({
						...error,
						customerId: new Error(message),
					})
				}
			} else {
				err.error = new Error(t('AuthenticationError'))
				setError(err)
			}
		}
		setLoading(false)
	}

	useEffect(() => {}, [error])

	if (loading) {
		return <div id="circle-spinner"></div>
	}

	const formatBirthDate = (str: string) => str.split('.').reverse().join('')

	return (
		<div className="login-container m-2 d-flex flex-column justify-content-between align-items-center">
			<div className="btn authButton GermanyOtp"></div>
			<div className="p-4 content">
				<div className="mobileId">
					<div className="mx-auto text-center">
						{!done && (
							<>
								{error.error && (
									<>
										{error.error.message ===
										'CardNotFoundContactSupport' ? (
											<div className="alert alert-danger otp-error-msg">
												{(() => {
													const emails =
														t(
															'CardNotFoundContactSupportEmails',
															{
																returnObjects:
																	true,
															},
														) || []
													return (
														<Trans
															i18nKey="CardNotFoundContactSupport"
															values={{
																email1:
																	emails[0] ||
																	'',
																email2:
																	emails[1] ||
																	'',
																email3:
																	emails[2] ||
																	'',
															}}
															components={createEmailComponents(
																t,
																'CardNotFoundContactSupportEmails',
																'CardNotFoundContactSupportAriaLabel',
															)}
														/>
													)
												})()}
											</div>
										) : (
											<ErrorMessageLegacy
												css="otp-error-msg"
												message={error.error.message}
											/>
										)}
									</>
								)}
								{secret ? (
									<div>
										<FormControl
											placeholder={t('Otp')}
											className="mt-2"
											name="otp"
											id="otp"
											autoFocus
											onChange={({
												currentTarget: { value },
											}) => setOtp(value)}
											isInvalid={!!error.otp}
											defaultValue={otp}
											type="tel"
											data-cy="otp"
											onKeyDown={(
												e: React.KeyboardEvent<HTMLInputElement>,
											) => {
												e.key === 'Enter' &&
													loginComplete()
											}}
										/>
										{error.otp && (
											<ValidationError
												css="otp-error-msg"
												message={error.otp.message}
											/>
										)}
									</div>
								) : (
									<>
										<div>
											<FormControl
												placeholder={t('LoginSspCode1')}
												className="mt-2"
												name="customerId"
												id="customerId"
												data-cy="customerId"
												onChange={({
													currentTarget: { value },
												}) => setCustomerId(value)}
												defaultValue={customerId}
												type="tel"
											/>
											<div className="description">
												<Trans
													t={t}
													i18nKey="CustomerIdDescription"
													components={[
														// eslint-disable-next-line jsx-a11y/anchor-has-content
														<a
															className="loginLink"
															href={loginLink}
															target="_blank"
															rel="noreferrer"
														/>,
													]}>
													Customer number not at hand?
													Then click{' '}
													<a
														className="loginLink"
														href="https://tfbank.de/blog/kundennummer/"
														target="_blank"
														rel="noreferrer">
														here
													</a>
													.
												</Trans>
											</div>
											{error.customerId && (
												<ValidationError
													css="otp-error-msg"
													message={
														error.customerId.message
													}
												/>
											)}
										</div>
										<div>
											<NumberFormat
												onValueChange={(value) =>
													setBirthDate(
														value.formattedValue,
													)
												}
												format="##.##.####"
												placeholder={t('Code2')}
												// prettier-ignore
												mask={['T','T','M','M','J','J','J','J']}
												type="tel"
												isNumericString={true}
												data-cy="birthDate"
												onKeyDown={(
													e: React.KeyboardEvent,
												) => {
													e.key === 'Enter' &&
														loginStart(
															formatBirthDate(
																birthDate,
															),
															customerId,
														)
												}}
												className="form-control"
											/>
											<div className="description">
												{t('BirthDateDescription')}
											</div>
											{error.birthDate && (
												<ValidationError
													css="otp-error-msg"
													message={
														error.birthDate.message
													}
												/>
											)}
										</div>
									</>
								)}
								<div className="d-flex justify-content-center mt-4 mb-1 mando">
									<Button
										variant="primary"
										data-cy="proceedButton"
										onClick={() =>
											secret
												? loginComplete()
												: loginStart(
														formatBirthDate(
															birthDate,
														),
														customerId,
													)
										}>
										{t('Proceed')}
									</Button>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

export default GermanyOtp
